// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";
import CompanySignatories from "../../../components/CompanySignatories/CompanySignatories";


export default class CobCustomerInformation extends React.PureComponent
{
    static propTypes = {
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        session: PropTypes.instanceOf(Session).isRequired,
        sectionNumber: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        prevState: PropTypes.object,
        onChange: PropTypes.func.isRequired
    };

    static companyExclusive = false;
    static privateExclusive = false;
    static fields = _getFields();

    state = _getInitialState.call(this);

    componentDidMount()
    {
        const {app} = this.props;
        const isFulfilled = fulfillmentCheck(this.props, this.state);
        const fulfilledViewsById = {...app.fulfilledViewsById};
        if (isFulfilled)
        {
            fulfilledViewsById["CustomerInformation"] = true;
        }
        else
        {
            delete fulfilledViewsById["CustomerInformation"];
        }
        this.props.actions.setAppState({fulfilledViewsById});
    }

    componentDidUpdate(prevProps, prevState)
    {
        const {props, state} = this;
        const {id, onChange} = props;

        if (prevState.fields !== state.fields)
        {
            onChange(id, {...state}, fulfillmentCheck(props, state));
        }
    }

    render()
    {
        const {props, state} = this;
        const {title, session} = props;
        const classes = createClassName("Cob-CustomerInformation", {
            "Cob-View": true,
            "fulfilled": state.isFulfilled
        });

        return (
            <div className={classes}>
                <DefaultFields fields={this.constructor.fields} />

                <div className="Cob-Fields">
                    <h2>{title}</h2>
                    <input type={"hidden"} name={"kyc_company"} value={session.isCompany ? "yes" : "no"} />

                    <FieldsCollection
                        fieldsPerRow={3}
                        fields={[
                            {
                                render: (
                                    <EditableField
                                        name="customer_firstname"
                                        label={session.isCompany ? "Företagets namn" : "Förnamn"}
                                        value={state.fields.customer_firstname}
                                        valid={(!!session.lookupData && state.lookupSuccessful) || state.validFields.customer_firstname}
                                        readOnly={state.lookupSuccessful}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required
                                    />
                                )
                            },
                            {
                                render: (
                                    <EditableField
                                        name="customer_lastname"
                                        label="Efternamn"
                                        value={state.fields.customer_lastname}
                                        valid={(!!session.lookupData && state.lookupSuccessful) || state.validFields.customer_lastname}
                                        readOnly={state.lookupSuccessful}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required
                                    />
                                ),
                                display: !session.isCompany
                            },
                            {
                                render: (
                                    <EditableField
                                        name="customer_social_security_number"
                                        label={session.isCompany ? "Organisationsnummer" : "Personnummer"}
                                        placeholder={"ÅÅÅÅÅMMDD-XXXX"}
                                        value={state.fields.customer_social_security_number}
                                        readOnly={state.lookupSuccessful}
                                        valid={true}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required
                                    />
                                )
                            }
                        ]}
                    />

                    <input type="hidden" name="customer_name" value={state.fields.customer_firstname + (session.isCompany ? "" : " " + state.fields.customer_lastname)} />
                    <input type="hidden" name="customername" value={state.fields.customer_firstname + (session.isCompany ? "" : " " + state.fields.customer_lastname)} />
                    <input type="hidden" name="customer_social_security_number" value={state.fields.customer_social_security_number} />

                    {session.isCompany && (
                        <CompanySignatories session={session} partner={this.props.partner}
                                            onChange={(valid) => CobHelpers.onViewFieldChange.call(this, "signatory_fields_valid", valid)} />
                    )}

                    {!session.isCompany && (
                        <React.Fragment>
                            <p className={"intro"} style={{marginBottom: "10px"}}>
                                Det digitala avtalet kommer att skickas till e-postadressen du anger nedan.
                            </p>

                            <EditableField
                                type="email"
                                name="customer_email"
                                label="E-postadress"
                                value={state.fields.customer_email}
                                valid={state.validFields.customer_email}
                                onChange={this._onFieldChange}
                                onBlur={this._onFieldBlur}
                                required
                            />
                        </React.Fragment>

                    )}
                </div>
            </div>
        );
    }

    // Internal methods
    _fieldValidator = (name, value) =>
    {
        if (name === "customer_social_security_number")
        {
            return Validator.validateSsn(value, true);
        }
        else if (name === "customer_email")
        {
            return Validator.validateEmail(value);
        }
        else
        {
            return value.length > 0;
        }
    };

    _onFieldChange = (name, value) =>
    {
        CobHelpers.onViewFieldChange.call(this, name, value);
    };

    _onFieldBlur = (name) =>
    {
        CobHelpers.onViewFieldBlur.call(this, name);
    };
}


// PRIVATE FUNCTIONS
function _getFields()
{
    return [
        "customer_firstname",
        "customer_lastname",
        "customer_social_security_number",
        "customer_email"
    ];
}

function _getInitialState()
{
    if (this.props.prevState)
    {
        return {...this.props.prevState};
    }

    const {session} = this.props;
    const getLookupData = (name) => session.lookupData ? session.lookupData[name] || "" : "";

    let initialState = {
        validFields: {
            customer_firstname: session.isCompany ? !!getLookupData("companyName") : !!getLookupData("firstName"),
            customer_lastname: session.isCompany ? true : !!getLookupData("lastName"),
            customer_social_security_number: session.isCompany ? !!getLookupData("orgNumber") : !!getLookupData("ssn"),
            customer_email: !!getLookupData("email") && Validator.validateEmail(getLookupData("email"))
        },
        fields: {
            customer_firstname: session.isCompany ? getLookupData("companyName") : getLookupData("firstName"),
            customer_lastname: getLookupData("lastName"),
            customer_social_security_number: session.isCompany ? getLookupData("orgNumber") : getLookupData("ssn"),
            customer_email: getLookupData("email"),
            signatory_fields_valid: false
        }
    };

    if (session.isCompany)
    {
        initialState.lookupSuccessful = initialState.fields.customer_firstname.length > 0;
        initialState.validFields.customer_email = true; // Not used for companies
    }
    else
    {
        initialState.lookupSuccessful = initialState.fields.customer_firstname.length > 0 && initialState.fields.customer_lastname.length > 0;
    }
    initialState.isCompany = session.isCompany;

    return initialState;
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/fair-kyc/CustomerInformation.js